import { useCallback, useEffect, useState } from 'react';
import { getBookingDetail, type BookingDetailResponse } from '../../api/bookings';
import { ApiError } from '../../api/client';
import {
  approveBooking,
  cancelBookingAsVendor,
  getStudioBookings,
  rejectBooking,
  type SpringPage,
  type StudioBookingListItem,
} from '../../api/partnerBookings';
import { useToast } from '../../components/ui/ToastProvider';
import { usePartnerContext } from './PartnerLayout';

const STATUS_OPTIONS: Array<{ label: string; value: string }> = [
  { label: '전체', value: '' },
  { label: '승인 대기', value: 'PENDING_APPROVAL' },
  { label: '확정', value: 'CONFIRMED' },
  { label: '결제 대기', value: 'PENDING_PAYMENT' },
  { label: '거절', value: 'REJECTED' },
  { label: '취소', value: 'CANCELLED' },
  { label: '완료', value: 'COMPLETED' },
];

const STATUS_LABELS: Record<string, string> = {
  PENDING_APPROVAL: '승인 대기',
  PENDING_PAYMENT: '결제 대기',
  CONFIRMED: '확정',
  APPROVED: '승인',
  REJECTED: '거절',
  CANCELLED: '취소',
  CANCELED: '취소',
  COMPLETED: '완료',
  EXPIRED: '만료',
  REFUNDED: '환불',
};

function statusBadgeClass(status: string) {
  if (status.includes('PENDING')) return 'partner-badge partner-badge--yellow';
  if (status === 'CONFIRMED' || status === 'APPROVED' || status === 'COMPLETED') {
    return 'partner-badge partner-badge--green';
  }
  if (status === 'REJECTED' || status.startsWith('CANCEL') || status === 'REFUNDED') {
    return 'partner-badge partner-badge--red';
  }
  return 'partner-badge';
}

function formatDateTime(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hour = String(parsed.getHours()).padStart(2, '0');
  const minute = String(parsed.getMinutes()).padStart(2, '0');
  return `${parsed.getFullYear()}.${month}.${day} ${hour}:${minute}`;
}

function formatWon(value: number | null | undefined) {
  return `${(value ?? 0).toLocaleString('ko-KR')}원`;
}

export function PartnerBookingsPage() {
  const { studios, studiosLoading, studiosError } = usePartnerContext();
  const { showToast } = useToast();

  const [studioId, setStudioId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [result, setResult] = useState<SpringPage<StudioBookingListItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BookingDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!studioId && studios.length > 0) {
      setStudioId(studios[0].studioId);
    }
  }, [studioId, studios]);

  const load = useCallback(async () => {
    if (!studioId) return;
    setLoading(true);
    setErrorMessage('');
    try {
      const data = await getStudioBookings(studioId, {
        page,
        size: 20,
        status: statusFilter ? [statusFilter] : undefined,
      });
      setResult(data);
    } catch (error) {
      setResult(null);
      setErrorMessage(
        error instanceof ApiError && error.message
          ? error.message
          : '예약 목록을 불러오지 못했습니다.',
      );
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, studioId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    getBookingDetail(detailId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) showToast('예약 상세를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailId, showToast]);

  const runAction = useCallback(
    async (bookingId: string, action: 'approve' | 'reject' | 'cancel') => {
      if (actingId) return;
      if (action === 'reject' && !window.confirm('이 예약을 거절할까요?')) return;
      let cancelReason = '';
      if (action === 'cancel') {
        const input = window.prompt('취소 사유를 입력하세요 (전액 환불됩니다).');
        if (input == null || !input.trim()) return;
        cancelReason = input.trim();
      }
      setActingId(bookingId);
      try {
        if (action === 'approve') {
          await approveBooking(bookingId);
          showToast('예약을 승인했습니다.');
        } else if (action === 'reject') {
          await rejectBooking(bookingId);
          showToast('예약을 거절했습니다.');
        } else {
          await cancelBookingAsVendor(bookingId, cancelReason);
          showToast('예약을 취소했습니다. 전액 환불 처리됩니다.');
        }
        await load();
      } catch (error) {
        showToast(
          error instanceof ApiError && error.message ? error.message : '처리에 실패했습니다.',
        );
      } finally {
        setActingId(null);
      }
    },
    [actingId, load, showToast],
  );

  if (studiosLoading) {
    return <p className="partner-empty">불러오는 중…</p>;
  }
  if (studiosError) {
    return <div className="partner-error">{studiosError}</div>;
  }
  if (studios.length === 0) {
    return <p className="partner-empty">등록된 스튜디오가 없습니다. 먼저 스튜디오를 등록하세요.</p>;
  }

  return (
    <>
      <h1 className="partner-title">예약 관리</h1>
      <p className="partner-subtitle">예약 요청을 승인·거절하고 확정 예약을 관리합니다.</p>

      <div className="partner-toolbar">
        <select
          aria-label="스튜디오 선택"
          className="partner-select"
          onChange={(event) => {
            setStudioId(event.target.value);
            setPage(0);
          }}
          value={studioId}
        >
          {studios.map((studio) => (
            <option key={studio.studioId} value={studio.studioId}>
              {studio.name}
            </option>
          ))}
        </select>
        <select
          aria-label="상태 필터"
          className="partner-select"
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPage(0);
          }}
          value={statusFilter}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value || 'ALL'} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="partner-toolbar__spacer" />
        <button className="partner-btn partner-btn--ghost partner-btn--sm" onClick={() => load()} type="button">
          새로고침
        </button>
      </div>

      {errorMessage ? <div className="partner-error">{errorMessage}</div> : null}

      <div className="partner-card">
        {loading ? (
          <p className="partner-empty">불러오는 중…</p>
        ) : !result || result.content.length === 0 ? (
          <p className="partner-empty">조건에 맞는 예약이 없습니다.</p>
        ) : (
          <>
            <div className="partner-table-wrap">
              <table className="partner-table">
                <thead>
                  <tr>
                    <th>예약번호</th>
                    <th>이용 시간</th>
                    <th>룸</th>
                    <th>금액</th>
                    <th>상태</th>
                    <th>처리</th>
                  </tr>
                </thead>
                <tbody>
                  {result.content.map((booking) => {
                    const acting = actingId === booking.bookingId;
                    const pending = booking.status === 'PENDING_APPROVAL';
                    const cancellable =
                      booking.status === 'CONFIRMED' || booking.status === 'APPROVED';
                    return (
                      <tr key={booking.bookingId}>
                        <td>
                          <button
                            className="partner-btn partner-btn--ghost partner-btn--sm"
                            onClick={() =>
                              setDetailId((prev) =>
                                prev === booking.bookingId ? null : booking.bookingId,
                              )
                            }
                            type="button"
                          >
                            {booking.bookingId}
                          </button>
                        </td>
                        <td>
                          {formatDateTime(booking.startsAt)}
                          <br />~ {formatDateTime(booking.endsAt)}
                        </td>
                        <td>{booking.roomId}</td>
                        <td>{formatWon(booking.totalPrice)}</td>
                        <td>
                          <span className={statusBadgeClass(booking.status)}>
                            {STATUS_LABELS[booking.status] ?? booking.status}
                          </span>
                        </td>
                        <td>
                          {pending ? (
                            <span style={{ display: 'inline-flex', gap: 6 }}>
                              <button
                                className="partner-btn partner-btn--sm"
                                disabled={acting}
                                onClick={() => runAction(booking.bookingId, 'approve')}
                                type="button"
                              >
                                승인
                              </button>
                              <button
                                className="partner-btn partner-btn--danger partner-btn--sm"
                                disabled={acting}
                                onClick={() => runAction(booking.bookingId, 'reject')}
                                type="button"
                              >
                                거절
                              </button>
                            </span>
                          ) : cancellable ? (
                            <button
                              className="partner-btn partner-btn--danger partner-btn--sm"
                              disabled={acting}
                              onClick={() => runAction(booking.bookingId, 'cancel')}
                              type="button"
                            >
                              강제 취소
                            </button>
                          ) : (
                            <span style={{ color: '#a3a9b5' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="partner-pagination">
              <button
                className="partner-btn partner-btn--ghost partner-btn--sm"
                disabled={result.first}
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                type="button"
              >
                이전
              </button>
              <span>
                {result.number + 1} / {Math.max(1, result.totalPages)} 페이지 · 총{' '}
                {result.totalElements}건
              </span>
              <button
                className="partner-btn partner-btn--ghost partner-btn--sm"
                disabled={result.last}
                onClick={() => setPage((prev) => prev + 1)}
                type="button"
              >
                다음
              </button>
            </div>
          </>
        )}
      </div>

      {detailId ? (
        <div className="partner-card">
          <h2 className="partner-card__title">
            예약 상세
            <button
              className="partner-btn partner-btn--ghost partner-btn--sm"
              onClick={() => setDetailId(null)}
              type="button"
            >
              닫기
            </button>
          </h2>
          {detailLoading ? (
            <p className="partner-empty">불러오는 중…</p>
          ) : detail ? (
            <dl className="partner-detail-grid">
              <div>
                <dt>예약번호</dt>
                <dd>{detail.bookingId}</dd>
              </div>
              <div>
                <dt>룸</dt>
                <dd>{detail.roomName}</dd>
              </div>
              <div>
                <dt>이용 시간</dt>
                <dd>
                  {formatDateTime(detail.startsAt)} ~ {formatDateTime(detail.endsAt)}
                </dd>
              </div>
              <div>
                <dt>예약자</dt>
                <dd>
                  {detail.bookerName} ({detail.bookerPhone})
                </dd>
              </div>
              <div>
                <dt>결제 금액</dt>
                <dd>{formatWon(detail.paidAmount ?? detail.totalPrice)}</dd>
              </div>
              <div>
                <dt>쿠폰 할인</dt>
                <dd>{formatWon(detail.couponDiscountAmount)}</dd>
              </div>
              <div>
                <dt>결제 수단</dt>
                <dd>{detail.paymentMethod ?? '—'}</dd>
              </div>
              <div>
                <dt>상태</dt>
                <dd>
                  <span className={statusBadgeClass(detail.status)}>
                    {STATUS_LABELS[detail.status] ?? detail.status}
                  </span>
                </dd>
              </div>
              {detail.cancelReason ? (
                <div>
                  <dt>취소 사유</dt>
                  <dd>{detail.cancelReason}</dd>
                </div>
              ) : null}
              {(detail.reservationAnswers ?? []).map((answer) => (
                <div key={answer.fieldId}>
                  <dt>{answer.title}</dt>
                  <dd>{answer.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="partner-empty">상세를 불러오지 못했습니다.</p>
          )}
        </div>
      ) : null}
    </>
  );
}
